import React from 'react';
import { Box, Stack } from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import {
    TextField,
    DurationField,
    CardList,
    ToggleButton,
} from '@travelity/form';
import { durationToString } from '@travelity/utils';
import { Tag } from '@travelity/ui';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as RotateLocation } from '../../assets/rotate-location.svg';
import {
    OnboardingClasses,
    OnboardingTours,
    useOnboardingTour,
} from '../../onboarding';
import { Onboarding } from '../../onboarding/onboarding.component';
import type { ProductRouteFormProps } from './product-route-form.types';
import { productRouteFormSchema } from './product-route-form.schema';
import { ProductRouteStepDistance } from './components/product-route-step-distance';

const ProductRouteForm: React.FC<ProductRouteFormProps> = ({
    preview,
    showFull,
    showErrors,
    isUpdate = true,
}) => {
    const { onboardingProps: routeItemOnboardingProps } = useOnboardingTour(
        OnboardingTours.ROUTE_ITEM
    );
    const { watch } = useFormContext();
    const { onboardingProps } = useOnboardingTour(OnboardingTours.ROUTE);

    const stops = watch('stops') || [];

    return (
        <>
            <ProductRouteStepDistance isEditing={preview} />
            <Stack
                spacing={2}
                sx={{ width: { xs: '100%', sm: '453px' } }}
                className={OnboardingClasses.ROUTE_CARDS}
            >
                <CardList
                    addClassName={OnboardingClasses.ADD_ROUTE_BUTTON}
                    actionButtonsClassName={
                        OnboardingClasses.FIRST_ROUTE_ACTIONS
                    }
                    showErrors={showErrors}
                    formParams={{ schema: productRouteFormSchema }}
                    disabled={preview}
                    maxHeight={showFull ? undefined : 356}
                    disableReorder
                    renderHeader={({ editing, item }, index) =>
                        editing ? (
                            <>
                                <TextField
                                    sx={{ mx: '10px' }}
                                    autoFocus
                                    InputProps={{
                                        sx: { fontSize: '14px', pl: 1.5 },
                                    }}
                                    placeholder="Type location name"
                                    name="name"
                                    variant="standard"
                                    showErrors
                                    helperText=""
                                    className={
                                        index === 0
                                            ? OnboardingClasses.FIRST_ROUTE_NAME
                                            : ''
                                    }
                                    fullWidth
                                />
                                {index === 0 && (
                                    <ToggleButton
                                        name="repeatLocation"
                                        className={
                                            OnboardingClasses.FIRST_ROUTE_ROUND_TRIP
                                        }
                                    >
                                        <RotateLocation />
                                    </ToggleButton>
                                )}
                            </>
                        ) : (
                            <Stack
                                sx={{ pl: 1, width: 1 }}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Box
                                    component="span"
                                    title={item.name}
                                    sx={{
                                        overflowX: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        lineHeight: 2,
                                    }}
                                >
                                    {item.name}
                                </Box>
                                {item.repeatLocation && (
                                    <Tag value="Round Trip" />
                                )}
                            </Stack>
                        )
                    }
                    renderContent={({ item, editing }, index) => (
                        <Stack sx={{ p: 1 }} direction="row" gap={1}>
                            <Box
                                sx={{
                                    width: '36px',
                                    height: '36px',
                                    color: 'primary.contrastText',
                                    bgcolor: 'primary.main',
                                    borderRadius: '12px',
                                    p: '6px',
                                    mb: 1,
                                    mt: editing ? 2 : 0.5,
                                }}
                            >
                                <AccessTime />
                            </Box>
                            <Stack
                                flexGrow={2}
                                sx={{ p: 0.5, pr: 1 }}
                                gap={0.5}
                            >
                                {!editing ? (
                                    <>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#a5a9b8',
                                                }}
                                            >
                                                Arrival Offset
                                            </Box>
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#2B395B',
                                                }}
                                            >
                                                {durationToString(
                                                    item.arrivalOffset
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#a5a9b8',
                                                }}
                                            >
                                                Stop Duration
                                            </Box>
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#2B395B',
                                                }}
                                            >
                                                {durationToString(
                                                    item.duration
                                                )}
                                            </Box>
                                        </Stack>
                                    </>
                                ) : (
                                    <>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            className={
                                                index === 0
                                                    ? OnboardingClasses.FIRST_ROUTE_ARRIVAL
                                                    : ''
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#a5a9b8',
                                                }}
                                            >
                                                Arrival Offset
                                            </Box>
                                            <Box>
                                                <DurationField
                                                    name="arrivalOffset"
                                                    showErrors
                                                />
                                            </Box>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            className={
                                                index === 0
                                                    ? OnboardingClasses.FIRST_ROUTE_DURATION
                                                    : ''
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: '12px',
                                                    color: '#a5a9b8',
                                                }}
                                            >
                                                Stop Duration
                                            </Box>
                                            <Box>
                                                <DurationField name="duration" />
                                            </Box>
                                        </Stack>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    )}
                    addButtonText="Add Stop"
                    name="stops"
                    sx={stops.length === 0 ? { mt: 2 } : {}}
                />
            </Stack>

            {!preview && !isUpdate && !stops.length && (
                <React.Fragment key="onboarding-empty">
                    <Onboarding {...onboardingProps} />
                </React.Fragment>
            )}
            {!preview && !isUpdate && !!stops.length && (
                <React.Fragment key="onboarding-items">
                    <Onboarding {...routeItemOnboardingProps} />
                </React.Fragment>
            )}
        </>
    );
};

export default React.memo(ProductRouteForm);
